/**
 * Compositional gesture primitives. Zero dependencies; thin wrapper over
 * native PointerEvent. Used by PartyOrbs's drag-to-reorder mechanic
 * (step 5b) — kept generic so future surfaces (long-press a sign for a
 * tooltip, drag a chip to filter the Pokédex, etc.) can reuse it.
 *
 * Parallel to input/intentRouter.ts — that one OR-merges direction
 * intent across input sources; this one composes pointer gestures
 * (long-press → drag) on a single element.
 *
 * Behavior contracts (load-bearing — change with care):
 *
 *   - onLongPress fires after `thresholdMs` of hold with pointer drift
 *     ≤ `driftPx`. Mouse pointers bypass the threshold per design pass
 *     6 (desktop drag-and-drop convention: hold-to-drag would feel
 *     wrong with a mouse).
 *   - onLongPress is cancelled by ANY of:
 *       1. pointermove with drift > driftPx
 *       2. pointerup before threshold (touch only; mouse never has a
 *          pre-threshold window)
 *       3. pointercancel
 *       4. a second pointerdown on the same target (multitouch defense
 *          — first touch wins; later touches ignored)
 *   - startDrag pins to start.pointerId. Pointermove / pointerup /
 *     pointercancel events with a DIFFERENT pointerId are ignored
 *     completely (no jumps when a second finger lands). Listeners
 *     attach at the WINDOW so the drag survives the pointer leaving
 *     target's bounds (orb-strip drag-outside scenarios).
 *   - Both functions return disposers. Calling the disposer mid-flight
 *     cancels cleanly without firing onEnd.
 */

/** Default long-press threshold (touch only). 300 ms is short enough
 *  that the gesture feels intentional but not deliberate. Matches the
 *  cartridge's drag-to-reorder convention in HGSS.  */
const DEFAULT_THRESHOLD_MS = 300;
/** Default max drift during a long-press hold. 8 px tolerates jitter
 *  from finger settling without accepting a deliberate pan. Matches
 *  WorldScene.handleTap's TAP_MAX_DRIFT_PX. */
const DEFAULT_DRIFT_PX = 8;

/** Default tap window. Matches joystick.ts's TAP_MAX_DURATION_MS gate so
 *  taps captured here resolve identically to ones the joystick zone
 *  passes through to WorldScene.handleTap. */
const DEFAULT_TAP_MAX_MS = 250;

export interface LongPressOpts {
  /** Hold duration before long-press fires. Defaults to 300 ms. Ignored
   *  for `pointerType === 'mouse'` — mouse fires immediately on the
   *  initial pointerdown so desktop drag-and-drop feels native. */
  thresholdMs?: number;
  /** Pointer drift (Euclidean px) allowed during the hold without
   *  cancelling. Defaults to 8 px. */
  driftPx?: number;
  /** Optional — fires on the originating pointerdown. Lets callers
   *  visualize a pending long-press at the moment of touch (e.g., a
   *  progress ring that should appear immediately, not at threshold).
   *  Multitouch-second-pointer events do NOT trigger onStart since
   *  the second pointer is ignored. v9 (draft card refresh PR 2). */
  onStart?: (e: PointerEvent) => void;
  /** Optional — fires when the long-press is cancelled BEFORE the
   *  threshold cb. Cancellation triggers: pointerup before threshold,
   *  pointercancel, pointerleave, drift > driftPx, or a second
   *  pointerdown on the same target (multitouch defense). Does NOT
   *  fire after the threshold cb has already run (that's success
   *  cleanup, not cancellation). v9 (draft card refresh PR 2). */
  onCancel?: () => void;
}

export interface DragHandlers {
  /** Fires on each pointermove for the pinned pointerId. dx/dy are
   *  relative to the start pointerdown's clientX/clientY. */
  onMove: (e: PointerEvent, dx: number, dy: number) => void;
  /** Fires once when the drag terminates — pointerup, pointercancel,
   *  or disposer call. `cancelled` is true on pointercancel OR
   *  disposer-while-active. */
  onEnd: (e: PointerEvent, dx: number, dy: number, cancelled: boolean) => void;
}

/**
 * Attach a long-press handler to `target`. Returns a disposer that
 * removes the listener.
 *
 * The detection state machine:
 *   pointerdown        → record (pointerId, x, y, t, type) + start timer
 *                        if pointerType !== 'mouse'. For mouse, the
 *                        timer fires immediately (queueMicrotask) so
 *                        callbacks land asynchronously like the touch
 *                        path (consistent ordering with startDrag).
 *   pointermove        → if drift > driftPx, cancel.
 *   pointerup          → if before threshold (touch), cancel. After
 *                        threshold + cancelled === false, the cb has
 *                        already fired and we just clean up.
 *   pointercancel      → cancel.
 *   second pointerdown → cancel the first (multitouch defense).
 *
 * The callback receives the ORIGINAL pointerdown event so consumers can
 * extract pointerId / clientX / clientY for downstream startDrag.
 */
export function onLongPress(
  target: HTMLElement,
  opts: LongPressOpts,
  cb: (start: PointerEvent) => void,
): () => void {
  const thresholdMs = opts.thresholdMs ?? DEFAULT_THRESHOLD_MS;
  const driftPx = opts.driftPx ?? DEFAULT_DRIFT_PX;

  interface Active {
    pointerId: number;
    startX: number;
    startY: number;
    timer: ReturnType<typeof setTimeout> | null;
    fired: boolean;
    pointerType: string;
  }
  let active: Active | null = null;

  function clear(): void {
    if (active?.timer !== null && active?.timer !== undefined) {
      clearTimeout(active.timer);
    }
    active = null;
  }

  /** Cancel an in-flight (pre-threshold) gesture. Fires onCancel IFF
   *  the gesture was actually in flight (active !== null) AND the cb
   *  hadn't already fired. Post-fire pointerup goes through clear()
   *  directly — that's success cleanup, not cancellation. */
  function cancel(): void {
    if (!active) return;
    const wasFired = active.fired;
    clear();
    if (!wasFired && opts.onCancel) opts.onCancel();
  }

  function onPointerDown(e: PointerEvent): void {
    if (active) {
      // Second pointer landed during an in-flight gesture. First wins;
      // cancel the original and ignore the new one (don't promote the
      // second pointer — that would let the user drag with a different
      // finger mid-press). Multitouch-second-pointer events trigger
      // onCancel for the original — caller may want to visually
      // reset (e.g., reverse a progress ring).
      cancel();
      return;
    }
    active = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      timer: null,
      fired: false,
      pointerType: e.pointerType ?? 'touch',
    };

    // onStart fires for every pointerdown that BECOMES the in-flight
    // gesture (the multitouch-second-pointer case is filtered above).
    // Synchronous so the caller's visualization (e.g., progress ring
    // start) lands at the moment of touch.
    if (opts.onStart) opts.onStart(e);

    if (e.pointerType === 'mouse') {
      // Mouse path: fire immediately. Microtask defer so the call site
      // sees consistent async timing across input types (touch always
      // crosses an event-loop boundary via the timer).
      const captured = active;
      const startEvent = e;
      queueMicrotask(() => {
        if (active === captured && !captured.fired) {
          captured.fired = true;
          cb(startEvent);
        }
      });
      return;
    }

    const captured = active;
    const startEvent = e;
    active.timer = setTimeout(() => {
      if (active === captured && !captured.fired) {
        captured.fired = true;
        cb(startEvent);
      }
    }, thresholdMs);
  }

  function onPointerMove(e: PointerEvent): void {
    if (!active || e.pointerId !== active.pointerId) return;
    const dx = e.clientX - active.startX;
    const dy = e.clientY - active.startY;
    if (Math.hypot(dx, dy) > driftPx) {
      cancel();
    }
  }

  function onPointerUp(e: PointerEvent): void {
    if (!active || e.pointerId !== active.pointerId) return;
    // Pre-threshold release is a cancel (touch only — mouse fires the
    // cb at pointerdown microtask, so by the time pointerup arrives,
    // fired is true). Post-fire release is just cleanup.
    if (!active.fired) {
      cancel();
    } else {
      clear();
    }
  }

  function onPointerCancel(e: PointerEvent): void {
    if (!active || e.pointerId !== active.pointerId) return;
    cancel();
  }

  target.addEventListener('pointerdown', onPointerDown);
  target.addEventListener('pointermove', onPointerMove);
  target.addEventListener('pointerup', onPointerUp);
  target.addEventListener('pointercancel', onPointerCancel);
  // pointerleave on the TARGET would also cancel — but the gestureRouter
  // contract is "long-press is on the target", so leaving the target
  // mid-press is a legitimate cancel. Some surfaces want this (orb-press
  // can't drift off the orb during the hold), so we include it.
  target.addEventListener('pointerleave', onPointerCancel);

  return () => {
    clear();
    target.removeEventListener('pointerdown', onPointerDown);
    target.removeEventListener('pointermove', onPointerMove);
    target.removeEventListener('pointerup', onPointerUp);
    target.removeEventListener('pointercancel', onPointerCancel);
    target.removeEventListener('pointerleave', onPointerCancel);
  };
}

/**
 * Begin a drag tracking session from a pointerdown event. Returns a
 * disposer that synthesizes an onEnd(cancelled: true) and removes the
 * window listeners. The disposer is idempotent — calling it after
 * natural pointerup is a no-op.
 *
 * PINS to start.pointerId — second-pointer events are ignored. Listeners
 * attach at window level so the drag continues past target boundaries
 * (essential for the orb-drag-outside-strip cancel path).
 */
export function startDrag(
  start: PointerEvent,
  handlers: DragHandlers,
): () => void {
  const pointerId = start.pointerId;
  const startX = start.clientX;
  const startY = start.clientY;
  let active = true;
  let lastEvent: PointerEvent = start;

  function delta(e: PointerEvent): { dx: number; dy: number } {
    return { dx: e.clientX - startX, dy: e.clientY - startY };
  }

  function onMove(e: PointerEvent): void {
    if (!active || e.pointerId !== pointerId) return;
    lastEvent = e;
    const { dx, dy } = delta(e);
    handlers.onMove(e, dx, dy);
  }

  function finish(e: PointerEvent, cancelled: boolean): void {
    if (!active) return;
    active = false;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    const { dx, dy } = delta(e);
    handlers.onEnd(e, dx, dy, cancelled);
  }

  function onUp(e: PointerEvent): void {
    if (!active || e.pointerId !== pointerId) return;
    finish(e, false);
  }

  function onCancel(e: PointerEvent): void {
    if (!active || e.pointerId !== pointerId) return;
    finish(e, true);
  }

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);

  return () => {
    if (!active) return;
    // Synthesize a cancel using the last-known event so coordinates are
    // sane. Callers commonly never invoke the disposer because pointerup
    // already finished the drag — only the orb-mounted teardown path
    // (PartyOrbs unmount mid-drag) actually hits this code.
    finish(lastEvent, true);
  };
}

export interface TapOpts {
  /** Maximum hold duration (ms) that still counts as a tap. Defaults
   *  to 250 ms — matches the joystick zone's tap-passthrough gate so
   *  taps resolve consistently across the canvas. */
  maxDurationMs?: number;
  /** Maximum drift (Euclidean px) during the hold without cancelling.
   *  Defaults to 8 px (matches DEFAULT_DRIFT_PX). */
  driftPx?: number;
}

/**
 * Attach a tap handler to `target`. Tap = pointerdown → pointerup with
 *  duration ≤ `maxDurationMs` AND total drift ≤ `driftPx`. Anything
 *  exceeding either bound (a hold or a drag) is silently ignored.
 *
 * Pins to the first pointerId so a second concurrent touch is treated
 * as multitouch and ignored — preserves the single-finger contract
 * established by onLongPress / startDrag.
 *
 * pointercancel + pointerleave both abort the in-flight tap (the
 * gesture left the target's bounds).
 *
 * Returns a disposer that detaches the listeners. Calling the disposer
 * mid-tap aborts cleanly without firing the callback.
 *
 * Used by the dialogue overlay (PR 2) for tap-to-advance, where the
 * overlay's z-index covers the canvas and the existing handleTap path
 * never sees the event. Generic primitive — other surfaces (sign
 * popovers, future tooltips) can reuse it.
 */
export function onTap(
  target: HTMLElement,
  opts: TapOpts,
  cb: (e: PointerEvent) => void,
): () => void {
  const maxMs = opts.maxDurationMs ?? DEFAULT_TAP_MAX_MS;
  const driftPx = opts.driftPx ?? DEFAULT_DRIFT_PX;

  interface Active {
    pointerId: number;
    startX: number;
    startY: number;
    startTime: number;
  }
  let active: Active | null = null;

  function onPointerDown(e: PointerEvent): void {
    if (active) {
      // Second pointer landed during an in-flight tap — multitouch
      // defense. First wins; cancel the original and ignore the new.
      active = null;
      return;
    }
    active = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startTime: e.timeStamp,
    };
  }

  function onPointerMove(e: PointerEvent): void {
    if (!active || e.pointerId !== active.pointerId) return;
    const dx = e.clientX - active.startX;
    const dy = e.clientY - active.startY;
    if (Math.hypot(dx, dy) > driftPx) {
      active = null;
    }
  }

  function onPointerUp(e: PointerEvent): void {
    if (!active || e.pointerId !== active.pointerId) return;
    const captured = active;
    active = null;
    const duration = e.timeStamp - captured.startTime;
    if (duration > maxMs) return;
    const dx = e.clientX - captured.startX;
    const dy = e.clientY - captured.startY;
    if (Math.hypot(dx, dy) > driftPx) return;
    cb(e);
  }

  function onPointerCancel(e: PointerEvent): void {
    if (!active || e.pointerId !== active.pointerId) return;
    active = null;
  }

  target.addEventListener('pointerdown', onPointerDown);
  target.addEventListener('pointermove', onPointerMove);
  target.addEventListener('pointerup', onPointerUp);
  target.addEventListener('pointercancel', onPointerCancel);
  target.addEventListener('pointerleave', onPointerCancel);

  return () => {
    active = null;
    target.removeEventListener('pointerdown', onPointerDown);
    target.removeEventListener('pointermove', onPointerMove);
    target.removeEventListener('pointerup', onPointerUp);
    target.removeEventListener('pointercancel', onPointerCancel);
    target.removeEventListener('pointerleave', onPointerCancel);
  };
}
